import { Box } from "arkh";
import React, { useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { WidgetEmployeeRoleOverview } from "widgets";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { getWidgetAddWidgetSizeWithinBounds } from "../utils/useWidgetAddWidgetSizeState";
import { WACategoryScreenProps } from "../WANavigation";
import { WACategoryWidgetContainer } from "../WACategoryWidgetContainer";
import { WACategoryEmployeeRolesQuery } from "./__generated__/WACategoryEmployeeRolesQuery.graphql";
import { overviewWidgetSizes } from "../utils/widgetSizes/overviewWidgetSizes";
import { FormattedMessage } from "react-intl";
import { useWACategoryLayout } from "../utils/useWACategoryLayout";

type Props = WACategoryScreenProps<"WACategoryEmployeeRoles">;
export function WACategoryEmployeeRoles(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerWACategoryEmployeeRoles {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWACategoryEmployeeRoles(props: Props & { fetchKey: string }) {
  const intl_tokens = useIntlTokens();

  const data = useLazyLoadQuery<WACategoryEmployeeRolesQuery>(
    graphql`
      query WACategoryEmployeeRolesQuery(
        $storecastId: ID!
        $input: PreviewWidgetEmployeeRoleOverviewInput!
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetEmployeeRoleOverview(input: $input) {
            ...WidgetEmployeeRoleOverview_widget
          }
        }
      }
    `,
    {
      input: {
        size: "TWO_X_TWO",
        teamIds: props.route.params.defaultTeams,
      },
      storecastId: props.route.params.storecastId,
    },
    { fetchKey: props.fetchKey }
  );

  const [size, hasSizeWithinBounds] = useMemo(
    () =>
      getWidgetAddWidgetSizeWithinBounds({
        maxSize: props.route.params.maxSize,
        widgetSizes: overviewWidgetSizes,
      }),
    [props.route.params.maxSize]
  );

  const onAdd = useCallback(() => {
    props.navigation.navigate("WidgetAddEmployeeRoles", {
      defaultTeams: props.route.params.defaultTeams,
      position: props.route.params.position,
      storecastId: props.route.params.storecastId,
      unitSize: props.route.params.unitSize,
      maxSize: props.route.params.maxSize,
    });
  }, [
    props.navigation,
    props.route.params.defaultTeams,
    props.route.params.maxSize,
    props.route.params.position,
    props.route.params.storecastId,
    props.route.params.unitSize,
  ]);

  const layout = useWACategoryLayout();

  return (
    <Box
      style={[
        layout,
        {
          flex: 1,
          justifyContent: "flex-start",
          alignItems: "center",
        },
      ]}
    >
      <WACategoryWidgetContainer
        hasSizeWithinBounds={hasSizeWithinBounds}
        size={size}
        title={intl_tokens.Lineup}
        unitSize={props.route.params.unitSize}
        onAdd={onAdd}
        addTitle={intl_tokens.SelectTeams}
        description={
          <FormattedMessage
            description="WA Employee Roles Description"
            defaultMessage="Display lineup with employees"
          />
        }
      >
        <WidgetEmployeeRoleOverview
          widget={data.dashboard.previewWidgetEmployeeRoleOverview}
          storecastId={props.route.params.storecastId}
          mode="IDLE"
          interactive={false}
        />
      </WACategoryWidgetContainer>
    </Box>
  );
}
